import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, resolveImageUrl } from "@/lib/axios";
import { Product, ProductCategory, ProductVariant } from "@/types";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft,
  Edit2,
  Trash2,
  DollarSign,
  Package,
  Layers,
  Sparkles,
  Upload,
  Check,
  Plus,
  ExternalLink,
} from "lucide-react";

export default function ProductDetailPageAdmin() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [product, setProduct] = useState<Product | null>(null);
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Imágenes y Variaciones
  const [parsedImages, setParsedImages] = useState<string[]>([]);
  const [parsedVariants, setParsedVariants] = useState<ProductVariant[]>([]);
  const [activeImage, setActiveImage] = useState<string>("");
  const [selectedVariant, setSelectedVariant] = useState<ProductVariant | null>(null);

  // Modal para Editar / Gestionar Variaciones
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editForm, setEditForm] = useState<Partial<Product>>({});
  const [variantDraft, setVariantDraft] = useState<ProductVariant>({
    name: "",
    type: "base",
    colorHex: "#B85D3B",
    imageUrl: "",
    priceAdjustment: 0,
  });

  // Modal para Confirmar Eliminación
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const [allProdsRes, catRes] = await Promise.all([
        api.get<Product[]>("/products?includeHidden=true").catch(() => ({ data: [] })),
        api.get<ProductCategory[]>("/product-categories").catch(() => ({ data: [] })),
      ]);

      const prods = Array.isArray(allProdsRes.data) ? allProdsRes.data : [];
      setAllProducts(prods);
      setCategories(Array.isArray(catRes.data) ? catRes.data : []);

      let targetId = id;
      if (!targetId || targetId === "detail") {
        if (prods.length > 0) {
          targetId = String(prods[0].id);
        }
      }

      if (!targetId || targetId === "detail") {
        setProduct(null);
        return;
      }

      const prodRes = await api.get<Product>(`/products/${targetId}`);
      const prod = prodRes.data;
      setProduct(prod);

      // Parsear imágenes
      let imgs: string[] = [];
      try {
        imgs = JSON.parse(prod.images || "[]");
      } catch {
        imgs = [];
      }
      imgs = imgs.map((img) => resolveImageUrl(img)).filter(Boolean);
      const mainImg = resolveImageUrl(prod.mainImageUrl);

      if (mainImg && !imgs.includes(mainImg)) {
        imgs = [mainImg, ...imgs];
      }
      setParsedImages(imgs);
      setActiveImage(mainImg || imgs[0] || "");

      // Parsear variantes
      let vars: ProductVariant[] = [];
      try {
        vars = JSON.parse(prod.variants || "[]");
      } catch {
        vars = [];
      }
      vars = vars.map((v) => ({ ...v, imageUrl: v.imageUrl ? resolveImageUrl(v.imageUrl) : v.imageUrl }));
      setParsedVariants(vars);
      if (vars.length > 0) {
        setSelectedVariant(vars[0]);
        if (vars[0].imageUrl) {
          setActiveImage(resolveImageUrl(vars[0].imageUrl));
        }
      }
    } catch {
      toast.error("No se pudo cargar el producto");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectVariant = (variant: ProductVariant) => {
    setSelectedVariant(variant);
    // Estilo MercadoLibre: al tocar una variación, cambiar foto principal
    if (variant.imageUrl) {
      setActiveImage(variant.imageUrl);
    }
  };

  const handleOpenEdit = () => {
    if (!product) return;
    setEditForm({
      ...product,
    });
    setIsEditModalOpen(true);
  };

  const handleAddVariant = () => {
    if (!variantDraft.name.trim()) {
      toast.error("Ingresa un nombre para la variación (ej. Base Plana, Blanco Hueso)");
      return;
    }

    const updated = [...parsedVariants, { ...variantDraft, id: Date.now().toString() }];
    setParsedVariants(updated);
    setVariantDraft({
      name: "",
      type: "base",
      colorHex: "#B85D3B",
      imageUrl: "",
      priceAdjustment: 0,
    });
    toast.success("Variación agregada a la lista");
  };

  const handleRemoveVariant = (index: number) => {
    const updated = parsedVariants.filter((_, i) => i !== index);
    setParsedVariants(updated);
  };

  const handleSaveProduct = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!product) return;

    try {
      const payload = {
        ...product,
        ...editForm,
        images: JSON.stringify(parsedImages),
        variants: JSON.stringify(parsedVariants),
      };

      await api.put(`/products/${product.id}`, payload);
      toast.success("Producto y variaciones guardados con éxito");
      setIsEditModalOpen(false);
      loadData();
    } catch {
      toast.error("Error al actualizar el producto");
    }
  };

  const handleDeleteProduct = async () => {
    if (!product) return;
    try {
      await api.delete(`/products/${product.id}`);
      toast.success("Producto eliminado del catálogo");
      navigate("/products-admin");
    } catch {
      toast.error("Error al eliminar el producto");
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24 text-muted-foreground">
        Cargando detalles del producto...
      </div>
    );
  }

  if (!product) {
    return (
      <div className="text-center py-20 space-y-4">
        <h2 className="text-2xl font-bold">Producto no encontrado</h2>
        <Button asChild variant="outline">
          <Link to="/products-admin">
            <ArrowLeft className="h-4 w-4 mr-2" /> Volver a Lista de Productos
          </Link>
        </Button>
      </div>
    );
  }

  const currentCategory =
    categories.find((c) => Number(c.id) === product.categoryId)?.name ||
    product.category?.name ||
    "General";

  const displayedPrice =
    (product.isOnSale && product.promotionalPrice ? product.promotionalPrice : product.salePrice) +
    (selectedVariant?.priceAdjustment || 0);

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-14 font-sans">
      {/* Barra Superior con Navegación y Acciones */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-2 border-b border-border/60">
        <div className="flex items-center gap-3">
          <Button asChild variant="ghost" size="icon" className="h-9 w-9 rounded-lg">
            <Link to="/products-admin">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">
                {product.name}
              </h1>
              {product.isPublic ? (
                <Badge variant="outline" className="text-xs bg-emerald-500/10 text-emerald-600 border-emerald-500/30">
                  Público
                </Badge>
              ) : (
                <Badge variant="secondary" className="text-xs">
                  Oculto
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              SKU: <span className="font-mono text-foreground">{product.sku || `ML-${product.id}`}</span> • Categoría:{" "}
              <span className="font-medium text-foreground">{currentCategory}</span>
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {allProducts.length > 1 && (
            <div className="flex items-center gap-1.5 bg-muted/40 border border-border rounded-lg px-2.5 py-1">
              <span className="text-[11px] text-muted-foreground whitespace-nowrap">Producto:</span>
              <select
                value={product.id}
                onChange={(e) => navigate(`/products-admin/${e.target.value}`)}
                className="text-xs bg-transparent font-medium text-foreground focus:outline-none cursor-pointer max-w-[170px] truncate"
              >
                {allProducts.map((p) => (
                  <option key={p.id} value={p.id} className="bg-popover text-popover-foreground">
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          <Button asChild variant="outline" size="sm" className="gap-1.5 text-xs">
            <Link to="/products-admin/new">
              <Plus className="h-3.5 w-3.5" /> Añadir Producto
            </Link>
          </Button>
          {product.slug && (
            <Button asChild variant="outline" size="sm" className="gap-1.5 text-xs">
              <Link to={`/tienda/${product.slug}`} target="_blank">
                <ExternalLink className="h-3.5 w-3.5" /> Ver en Tienda
              </Link>
            </Button>
          )}
          <Button onClick={handleOpenEdit} size="sm" className="gap-1.5 text-xs">
            <Edit2 className="h-3.5 w-3.5" /> Editar & Variaciones
          </Button>
          <Button
            onClick={() => setIsDeleteModalOpen(true)}
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground hover:text-destructive"
            title="Eliminar producto"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* 4 KPI Cards Estilo Shadcn UI Kit */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-border/80 shadow-xs">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">Precio de Venta</p>
              <h3 className="text-2xl font-bold text-foreground mt-0.5">
                ${product.salePrice.toLocaleString("es-AR")}
              </h3>
            </div>
            <div className="p-2.5 rounded-xl bg-primary/10 text-primary">
              <DollarSign className="h-5 w-5" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/80 shadow-xs">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">Costo de Fabricación</p>
              <h3 className="text-2xl font-bold text-foreground mt-0.5">
                ${product.costPrice ? product.costPrice.toLocaleString("es-AR") : "0"}
              </h3>
            </div>
            <div className="p-2.5 rounded-xl bg-amber-500/10 text-amber-600">
              <Layers className="h-5 w-5" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/80 shadow-xs">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">Peso Estimado (3D)</p>
              <h3 className="text-2xl font-bold text-foreground mt-0.5">
                {product.weightGrams ? `${product.weightGrams}g` : "N/D"}
              </h3>
            </div>
            <div className="p-2.5 rounded-xl bg-blue-500/10 text-blue-600">
              <Package className="h-5 w-5" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/80 shadow-xs">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">Tiempo de Impresión</p>
              <h3 className="text-2xl font-bold text-foreground mt-0.5">
                {product.printTimeHours ? `${product.printTimeHours}hs` : "N/D"}
              </h3>
            </div>
            <div className="p-2.5 rounded-xl bg-purple-500/10 text-purple-600">
              <Sparkles className="h-5 w-5" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Grid Central: Visor Interactivo MercadoLibre + Especificaciones */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        {/* Columna Izquierda: Galería y Foto Dinámica (7 cols) */}
        <div className="lg:col-span-7 space-y-4">
          <Card className="overflow-hidden border-border/80 shadow-md bg-card">
            <div className="aspect-square sm:aspect-4/3 relative bg-muted/40 flex items-center justify-center overflow-hidden">
              {activeImage ? (
                <img
                  src={activeImage}
                  alt={product.name}
                  className="w-full h-full object-cover transition-all duration-300"
                />
              ) : (
                <div className="text-muted-foreground text-sm flex flex-col items-center gap-2">
                  <Package className="h-16 w-16 opacity-30" />
                  <span>Sin imagen configurada</span>
                </div>
              )}

              {product.isOnSale && (
                <Badge className="absolute top-4 left-4 bg-destructive text-destructive-foreground text-xs font-semibold shadow-md">
                  EN OFERTA
                </Badge>
              )}
            </div>

            {/* Carrusel de Miniaturas */}
            <div className="p-3 bg-muted/20 border-t border-border/60 flex items-center gap-2 overflow-x-auto">
              {parsedImages.map((imgUrl, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setActiveImage(imgUrl)}
                  className={`w-16 h-16 rounded-lg overflow-hidden border-2 shrink-0 transition-all ${
                    activeImage === imgUrl
                      ? "border-primary ring-2 ring-primary/30"
                      : "border-border/60 opacity-70 hover:opacity-100"
                  }`}
                >
                  <img src={imgUrl} alt={`Thumb ${i}`} className="w-full h-full object-cover" />
                </button>
              ))}

              {/* Botón para añadir foto extra */}
              <label className="cursor-pointer shrink-0">
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const formData = new FormData();
                    formData.append("file", file);
                    try {
                      toast.loading("Subiendo foto...");
                      const res = await api.post("/uploads", formData, {
                        headers: { "Content-Type": "multipart/form-data" },
                      });
                      toast.dismiss();
                      const fullUrl = resolveImageUrl(res.data.url);
                      const updated = [...parsedImages, fullUrl];
                      setParsedImages(updated);
                      setActiveImage(fullUrl);
                      // Guardar en backend
                      await api.put(`/products/${product.id}`, {
                        ...product,
                        images: JSON.stringify(updated),
                      });
                      toast.success("Foto añadida al producto");
                    } catch {
                      toast.dismiss();
                      toast.error("Error al subir foto");
                    }
                  }}
                />
                <div className="w-16 h-16 rounded-lg border-2 border-dashed border-border flex flex-col items-center justify-center text-muted-foreground hover:border-primary transition-colors text-[10px]">
                  <Plus className="h-4 w-4" />
                  <span>Foto</span>
                </div>
              </label>
            </div>
          </Card>
        </div>

        {/* Columna Derecha: Configuración Interactiva & Variaciones (5 cols) */}
        <div className="lg:col-span-5 space-y-6">
          <Card className="border-border/80 shadow-xs">
            <CardContent className="p-6 space-y-6">
              <div>
                <h3 className="text-xl font-bold tracking-tight text-foreground">
                  Opciones & Variaciones
                </h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Toca una opción para previsualizar su foto asociada (estilo MercadoLibre)
                </p>
              </div>

              {/* Selector de Variaciones con cambio de foto */}
              <div className="space-y-3">
                <div className="flex justify-between items-center text-xs">
                  <span className="font-semibold text-foreground">Seleccionar Variante:</span>
                  <span className="text-primary font-medium">
                    {selectedVariant?.name || "Predeterminada"}
                  </span>
                </div>

                {parsedVariants.length === 0 ? (
                  <div className="p-4 border border-dashed rounded-xl text-center text-xs text-muted-foreground space-y-2">
                    <p>No hay variantes cargadas para este producto aún.</p>
                    <Button
                      onClick={handleOpenEdit}
                      variant="outline"
                      size="sm"
                      className="text-xs gap-1"
                    >
                      <Plus className="h-3.5 w-3.5" /> Configurar Variaciones
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2.5">
                    {parsedVariants.map((v, i) => {
                      const isSelected = selectedVariant?.name === v.name;
                      return (
                        <button
                          key={i}
                          type="button"
                          onClick={() => handleSelectVariant(v)}
                          className={`p-3 rounded-xl border text-left transition-all flex items-center justify-between ${
                            isSelected
                              ? "border-primary bg-primary/5 text-foreground ring-1 ring-primary/40 font-medium"
                              : "border-border/70 hover:bg-muted/40 text-muted-foreground"
                          }`}
                        >
                          <div className="flex items-center gap-2.5 truncate">
                            {v.colorHex && (
                              <div
                                className="h-4 w-4 rounded-full border shadow-2xs shrink-0"
                                style={{ backgroundColor: v.colorHex }}
                              />
                            )}
                            <div className="truncate">
                              <p className="text-xs font-semibold truncate leading-tight">
                                {v.name}
                              </p>
                              {v.priceAdjustment ? (
                                <span className="text-[10px] text-muted-foreground font-mono">
                                  {v.priceAdjustment > 0 ? `+$${v.priceAdjustment}` : `-$${Math.abs(v.priceAdjustment)}`}
                                </span>
                              ) : null}
                            </div>
                          </div>
                          {isSelected && <Check className="h-4 w-4 text-primary shrink-0" />}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Precio Resultante */}
              <div className="p-4 bg-muted/30 rounded-xl border border-border/60 flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Precio Final al Cliente</p>
                  <p className="text-2xl font-extrabold text-primary">
                    ${displayedPrice.toLocaleString("es-AR")}
                  </p>
                </div>
                <Badge variant="outline" className="text-xs font-mono">
                  {product.estimatedDeliveryDays || 5} días entrega
                </Badge>
              </div>

              {/* Ficha Técnica y Especificaciones */}
              <div className="space-y-3 pt-2 border-t border-border/60 text-xs">
                <h4 className="font-semibold text-foreground text-sm">Ficha Técnica</h4>
                <div className="divide-y divide-border/50">
                  <div className="py-2 flex justify-between">
                    <span className="text-muted-foreground">Categoría</span>
                    <span className="font-medium text-foreground">{currentCategory}</span>
                  </div>
                  <div className="py-2 flex justify-between">
                    <span className="text-muted-foreground">Visibilidad Tienda</span>
                    <span className="font-medium text-foreground">
                      {product.isPublic ? "Visible" : "Oculto"}
                    </span>
                  </div>
                  <div className="py-2 flex justify-between">
                    <span className="text-muted-foreground">Promoción / Oferta</span>
                    <span className="font-medium text-foreground">
                      {product.isOnSale ? "Activa" : "Inactiva"}
                    </span>
                  </div>
                  <div className="py-2 flex justify-between">
                    <span className="text-muted-foreground">Descripción Corta</span>
                    <span className="font-medium text-foreground text-right max-w-[200px] truncate">
                      {product.shortDescription || "Sin descripción corta"}
                    </span>
                  </div>
                  <div className="py-2 flex justify-between">
                    <span className="text-muted-foreground">Stock Inmediato</span>
                    <span className="font-semibold text-foreground">
                      {product.stockQuantity > 0 ? (
                        <span className="text-emerald-600 font-bold">{product.stockQuantity} unidades</span>
                      ) : product.allowBackorder ? (
                        <span className="text-amber-600">0 u. (A pedido)</span>
                      ) : (
                        <span className="text-destructive font-bold">Agotado (0 u.)</span>
                      )}
                    </span>
                  </div>
                  <div className="py-2 flex justify-between">
                    <span className="text-muted-foreground">Tiempo de Fabricación / Envío</span>
                    <span className="font-medium text-foreground">
                      {product.estimatedDeliveryDays || 5} días hábiles
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Modal para Editar Datos y Gestionar Variaciones */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <form onSubmit={handleSaveProduct}>
            <DialogHeader>
              <DialogTitle>Editar Producto & Configurar Variaciones</DialogTitle>
            </DialogHeader>

            <div className="space-y-5 py-4">
              {/* Datos Básicos */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Nombre *</Label>
                  <Input
                    required
                    value={editForm.name || ""}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  />
                </div>
                <div className="space-y-1">
                  <Label>Categoría</Label>
                  <select
                    className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-xs"
                    value={editForm.categoryId || (categories[0]?.id ?? 1)}
                    onChange={(e) =>
                      setEditForm({ ...editForm, categoryId: Number(e.target.value) })
                    }
                  >
                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Precio de Venta ($) *</Label>
                  <Input
                    type="number"
                    required
                    value={editForm.salePrice || 0}
                    onChange={(e) =>
                      setEditForm({ ...editForm, salePrice: Number(e.target.value) })
                    }
                  />
                </div>
                <div className="space-y-1">
                  <Label>Costo Base ($)</Label>
                  <Input
                    type="number"
                    value={editForm.costPrice || 0}
                    onChange={(e) =>
                      setEditForm({ ...editForm, costPrice: Number(e.target.value) })
                    }
                  />
                </div>
              </div>

              {/* Inventario & Logística de Envío */}
              <div className="p-3.5 bg-muted/30 rounded-xl space-y-3 border border-border/70">
                <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                  📦 Inventario & Tiempo de Envío
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs font-semibold">Stock Físico Inmediato (u.)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={editForm.stockQuantity ?? 0}
                      onChange={(e) =>
                        setEditForm({ ...editForm, stockQuantity: Number(e.target.value) })
                      }
                    />
                    <p className="text-[10px] text-muted-foreground">Unidades listas para entrega</p>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs font-semibold">Tiempo de Envío / Fabricación (días) *</Label>
                    <Input
                      type="number"
                      min="1"
                      value={editForm.estimatedDeliveryDays ?? 5}
                      onChange={(e) =>
                        setEditForm({ ...editForm, estimatedDeliveryDays: Number(e.target.value) })
                      }
                    />
                    <p className="text-[10px] text-muted-foreground">Días hábiles si se fabrica a pedido</p>
                  </div>
                </div>

                <div className="flex items-center gap-2 pt-1 border-t border-border/40">
                  <input
                    type="checkbox"
                    id="allowBackorderAdminCheck"
                    className="rounded h-4 w-4"
                    checked={editForm.allowBackorder ?? true}
                    onChange={(e) =>
                      setEditForm({ ...editForm, allowBackorder: e.target.checked })
                    }
                  />
                  <Label htmlFor="allowBackorderAdminCheck" className="text-xs cursor-pointer">
                    Permitir compra bajo demanda cuando no haya stock físico
                  </Label>
                </div>
              </div>

              {/* SECCIÓN VARIACIONES ESTILO MERCADOLIBRE */}
              <div className="space-y-3 pt-3 border-t border-border">
                <div>
                  <h4 className="font-semibold text-sm text-foreground flex items-center gap-1.5">
                    <Layers className="h-4 w-4 text-primary" /> Variaciones con Fotos Específicas
                  </h4>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Asocia una foto a cada variación para que se actualice al hacer clic en ella.
                  </p>
                </div>

                {/* Lista actual de variantes configuradas */}
                {parsedVariants.length > 0 && (
                  <div className="space-y-2">
                    {parsedVariants.map((v, idx) => (
                      <div
                        key={idx}
                        className="p-3 bg-muted/30 border border-border/70 rounded-xl flex items-center justify-between text-xs"
                      >
                        <div className="flex items-center gap-3">
                          {v.imageUrl ? (
                            <img
                              src={v.imageUrl}
                              alt={v.name}
                              className="w-10 h-10 rounded-md object-cover border"
                            />
                          ) : (
                            <div
                              className="w-10 h-10 rounded-md border flex items-center justify-center text-[10px] text-muted-foreground"
                              style={{ backgroundColor: v.colorHex || "#eee" }}
                            >
                              Color
                            </div>
                          )}
                          <div>
                            <p className="font-semibold text-foreground text-sm">{v.name}</p>
                            <p className="text-[11px] text-muted-foreground">
                              {v.imageUrl ? "Con foto asociada" : "Sin foto"} •{" "}
                              {v.priceAdjustment ? `Ajuste: $${v.priceAdjustment}` : "Mismo precio"}
                            </p>
                          </div>
                        </div>

                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-8 text-destructive hover:bg-destructive/10"
                          onClick={() => handleRemoveVariant(idx)}
                        >
                          Eliminar
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Formulario para agregar nueva variación */}
                <div className="p-3 bg-card border border-border/80 rounded-xl space-y-3">
                  <p className="text-xs font-semibold text-foreground">Agregar Nueva Variación:</p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <Input
                      className="text-xs"
                      placeholder="Nombre (ej. Base Plana / Blanco)"
                      value={variantDraft.name}
                      onChange={(e) =>
                        setVariantDraft({ ...variantDraft, name: e.target.value })
                      }
                    />
                    <Input
                      className="text-xs"
                      type="number"
                      placeholder="Ajuste de precio ($)"
                      value={variantDraft.priceAdjustment || ""}
                      onChange={(e) =>
                        setVariantDraft({
                          ...variantDraft,
                          priceAdjustment: Number(e.target.value),
                        })
                      }
                    />
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={variantDraft.colorHex || "#B85D3B"}
                        onChange={(e) =>
                          setVariantDraft({ ...variantDraft, colorHex: e.target.value })
                        }
                        className="h-8 w-10 cursor-pointer rounded border p-0.5 shrink-0"
                        title="Color representativo"
                      />
                      <span className="text-[11px] text-muted-foreground truncate">Color</span>
                    </div>
                  </div>

                  {/* Carga de foto para esta variación */}
                  <div className="flex items-center gap-2 pt-1">
                    <Input
                      className="text-xs flex-1"
                      placeholder="URL de foto o sube un archivo..."
                      value={variantDraft.imageUrl || ""}
                      onChange={(e) =>
                        setVariantDraft({ ...variantDraft, imageUrl: e.target.value })
                      }
                    />
                    <label className="cursor-pointer shrink-0">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          const formData = new FormData();
                          formData.append("file", file);
                          try {
                            toast.loading("Subiendo foto de variación...");
                            const res = await api.post("/uploads", formData, {
                              headers: { "Content-Type": "multipart/form-data" },
                            });
                            toast.dismiss();
                            const fullUrl = resolveImageUrl(res.data.url);
                            setVariantDraft({ ...variantDraft, imageUrl: fullUrl });
                            toast.success("Foto asignada a la variación");
                          } catch {
                            toast.dismiss();
                            toast.error("Error al subir foto");
                          }
                        }}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        asChild
                        className="gap-1 text-xs pointer-events-none"
                      >
                        <span>
                          <Upload className="h-3.5 w-3.5" /> Subir Foto
                        </span>
                      </Button>
                    </label>

                    <Button
                      type="button"
                      size="sm"
                      onClick={handleAddVariant}
                      className="text-xs shrink-0"
                    >
                      Añadir
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsEditModalOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit">Guardar Cambios</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal Confirmar Eliminación */}
      <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>¿Eliminar este producto?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground py-2">
            El producto se desactivará de la tienda y del catálogo. Podrás reactivarlo en cualquier momento.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteModalOpen(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleDeleteProduct}>
              Sí, eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
